import React from "react";
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import { Tooltip } from "@mui/material";
import { useDispatch, useSelector } from "react-redux";
import { ApplicationDispatch } from "../../../store/application/types";
import { useBusinessRulesEngine } from "../../../Data/BusinessRulesData";
import {
  customerCreditSections,
  ICustomerCredit, ICustomerCreditRow,
  ICustomerCreditSection
} from "../../../Interfaces/Customer/ICustomerCredit";
import { TextBox } from "@progress/kendo-react-inputs";
import produce from "immer";
import { updateEditingApp } from "../../../store/application/actions";
import { AppState } from "../../../store/rootReducer";
import { BureauFavorite } from "../../../Data/BureauFavorites";
import { ICustomerCustomField } from "../../../Interfaces/Customer/ICustomerCustomField";
import { IApplicationCustomField } from "../../../Interfaces/Application/IApplicationCustomField";
import { IEntityCustomField } from "../../../Interfaces/Application/IEntityCustomField";
import './_customer-credit-page.module.scss';

// Name/value pairs for the customer credit fields that the user can edit and/or override.
interface ICustomerCreditUpdatePair {
  variableName: string;
  value?: number;
}

interface ICustomerCreditPageProps {
  customerCredit: ICustomerCredit;
  customFields: IApplicationCustomField[] | ICustomerCustomField[];
  favorites: BureauFavorite[];
}

interface FavoritesSectionProps {
  customerCredit: ICustomerCredit;
  customFields: IApplicationCustomField[] | ICustomerCustomField[];
  favorites: BureauFavorite[];
  onUpdateCustomerCredit(field: ICustomerCreditUpdatePair): void;
  onUpdateOverrideValue(field: ICustomerCreditUpdatePair): void;
}

const BureauFavoriteSection: React.FC<FavoritesSectionProps> = (props) => {
  const allowUserToOverrideValue = (favoriteName: string): boolean => {
    // find the variable name of the favorite, then use that to see if the custom field is overridable
    const variableName = customerCreditSections.find(section => section.customerCreditRows.some(row => row.itemTitle.toLowerCase() === favoriteName.toLowerCase()))!.customerCreditRows.find(row => row.itemTitle.toLowerCase() === favoriteName.toLowerCase())!.variableName;
    return props.customFields.some((cf: IEntityCustomField) => cf.customField.customerCreditOverrideField?.toLowerCase() === variableName.toLowerCase());
  }

  return (
    <div style={{ display: 'flex', flexDirection: 'column', border: '1px solid gray', padding: '7px', borderRadius: '5px', maxWidth: '600px', minWidth: '0' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1em', borderBottom: '1px solid #e0e0e0', maxWidth: '600px' }}>
        <div style={{ display: 'flex', flexShrink: 1, alignItems: 'center', fontSize: '1.1em', minWidth: '160px', width: '300px', maxWidth: '300px' }}><b>Favorites</b></div>
        <div style={{ display: 'flex', alignItems: 'center', maxWidth: '120px', minWidth: '90px' }}>Bureau</div>
        <div style={{ display: 'flex', alignItems: 'center', maxWidth: '120px', minWidth: '90px' }}>Override</div>
      </div>
      {props.favorites.sort((a, b) => Number(a.sortOrder) - Number(b.sortOrder)).map((favorite, key) => {
        return (
          <div key={key} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1em', maxWidth: '600px' }}>
            <div style={{ display: 'flex', flexShrink: 1, alignItems: 'center', minWidth: '160px', width: '300px', maxWidth: '300px', fontSize: '0.9em' }}>
              <Tooltip title={customerCreditSections.find(section => section.customerCreditRows.some(row => row.itemTitle.toLowerCase() === favorite.fieldName.toLowerCase()))!.customerCreditRows.find(row => row.itemTitle.toLowerCase() === favorite.fieldName.toLowerCase())!.toolTipText}>
                <InfoOutlinedIcon
                  fontSize="small"
                  color="primary"
                  style={{ marginRight: '8px', cursor: 'help' }}
                />
              </Tooltip>
              {favorite.fieldName}
            </div>
            <div style={{ maxWidth: '120px', minWidth: '90px' }}>
              <TextBox
                value={(props.customerCredit[customerCreditSections.find(section => section.customerCreditRows.some(row => row.itemTitle.toLowerCase() === favorite.fieldName.toLowerCase()))!.customerCreditRows.find(row => row.itemTitle.toLowerCase() === favorite.fieldName.toLowerCase())!.variableName] as string | number | readonly string[] | undefined) ?? ''}
                style={{ cursor: 'not-allowed', fontSize: '0.9em' }}
                readOnly={true}
                title='Bureau field is not editable'
                size={'small'}
              />
            </div>
            <div style={allowUserToOverrideValue(favorite.fieldName) ? { maxWidth: '120px', minWidth: '90px' } : { maxWidth: '120px', minWidth: '90px', visibility: 'hidden' }}>
              <TextBox
                value={(props.customFields as IEntityCustomField[]).find(cf => cf.customField.customerCreditOverrideField?.toLowerCase() === customerCreditSections.find(section => section.customerCreditRows.some(row => row.itemTitle.toLowerCase() === favorite.fieldName.toLowerCase()))!.customerCreditRows.find(row => row.itemTitle.toLowerCase() === favorite.fieldName.toLowerCase())!.variableName.toLowerCase())?.value ?? ''}
                onChange={(e) => props.onUpdateOverrideValue({ variableName: customerCreditSections.find(section => section.customerCreditRows.some(row => row.itemTitle.toLowerCase() === favorite.fieldName.toLowerCase()))!.customerCreditRows.find(row => row.itemTitle.toLowerCase() === favorite.fieldName.toLowerCase())!.variableName, value: e.target.value ? Number(e.target.value) : undefined })}
                readOnly={!allowUserToOverrideValue(favorite.fieldName)}
                title={`Enter a value here to override the value for ${favorite.fieldName}`}
                size={'small'}
                style={{ fontSize: '0.9em' }}
                type="number"
              />
            </div>
          </div>
        );
      }
      )}
    </div>
  );
};

export const CustomerCreditPage: React.FC<ICustomerCreditPageProps> = (props) => {
  const currentApp = useSelector((state: AppState) => state.applications.editingApp)!;
  const dispatch = useDispatch<ApplicationDispatch>();
  const businessRulesEngine = useBusinessRulesEngine();

  // User has changed the value of the "Bureau" input field.
  const onUpdateCustomerCredit = (row: ICustomerCreditUpdatePair) => {
    const updatedApp = produce(currentApp, (currentApp) => {
      const customerCreditToUpdate = currentApp.appCustomers
        .find(ac => ac.customerId === props.customerCredit.customerId)
        ?.customer.customerCredit.find(cc => cc.id === props.customerCredit.id);
      if (customerCreditToUpdate) {
        customerCreditToUpdate[row.variableName] = row.value;
      }
    });
    dispatch(updateEditingApp(updatedApp!, businessRulesEngine));
  }

  // User has changed the value of the "Override" input field.
  const onUpdateOverrideValue = (row: ICustomerCreditUpdatePair) => {
    const isCustomerCustomField: boolean = (props.customFields as IEntityCustomField[]).find(cf => cf.customField.customerCreditOverrideField?.toLowerCase() === row.variableName.toLowerCase())?.customField.isCustomerCustomField ?? false;
    const updatedApp = produce(currentApp, (currentApp) => {
      if (isCustomerCustomField) {
        const customerToUpdate = currentApp.appCustomers.find(ac => ac.customerId.toLowerCase() === props.customerCredit.customerId.toLowerCase());
        if (customerToUpdate) {
          const customFieldToUpdate = customerToUpdate.customer.customerCustomFields.find(cf => cf.customField.customerCreditOverrideField?.toLowerCase() === row.variableName.toLowerCase());
          if (customFieldToUpdate) {
            console.log('updating customer custom field', row.value);
            customFieldToUpdate.value = row.value?.toString() ?? '';
          }
        }
      } else {
        // find and update the application custom field
        const customFieldToUpdate = currentApp.applicationCustomFields.find(cf => cf.customField.customerCreditOverrideField?.toLowerCase() === row.variableName.toLowerCase());
        if (customFieldToUpdate) {
          customFieldToUpdate.value = row.value?.toString() ?? '';
        }
      }
    });
    dispatch(updateEditingApp(updatedApp!, businessRulesEngine));
  }

  // filters out the favorites from the customer credit sections
  const getCustomerCreditSectionsWithoutFavorites = () => {
    return customerCreditSections.map((section) => {
      return produce(section, (draft) => {
        draft.customerCreditRows = draft.customerCreditRows.filter((row) => {
          return !props.favorites.some((favorite) => favorite.fieldName.toLowerCase() === row.itemTitle.toLowerCase());
        });
      });
    });
  }

  return (
    <div style={{
      display: 'flex',
      flex: 1,
      flexDirection: 'column',
      overflowY: 'auto',
      padding: '5px 5px 2em 5px' // extra padding at the bottom to prevent the last row from being cut off by the text message tab
    }}>
      {props.favorites.length > 0 && (
        <BureauFavoriteSection
          customerCredit={props.customerCredit}
          customFields={props.customFields}
          favorites={props.favorites}
          onUpdateCustomerCredit={onUpdateCustomerCredit}
          onUpdateOverrideValue={onUpdateOverrideValue}
        />
      )}
      {getCustomerCreditSectionsWithoutFavorites().map((section: ICustomerCreditSection, key) => {
        return (
          <CustomerCreditSection
            {...props}
            index={key}
            customerCreditSection={section}
            customFields={props.customFields}
            onUpdateCustomerCredit={onUpdateCustomerCredit}
            onUpdateOverrideValue={onUpdateOverrideValue}
          />
        );
      })}
    </div>
  )
}

interface ICustomerCreditSectionProps {
  index: number;
  customerCredit: ICustomerCredit;
  customFields: IApplicationCustomField[] | ICustomerCustomField[];
  customerCreditSection: ICustomerCreditSection;
  onUpdateCustomerCredit(field: ICustomerCreditUpdatePair): void;
  onUpdateOverrideValue(field: ICustomerCreditUpdatePair): void;
}

const CustomerCreditSection: React.FC<ICustomerCreditSectionProps> = (props) => {
  return (
    <div key={props.index} style={{ display: 'flex', flexDirection: 'column', marginTop: '5px', border: '1px solid gray', padding: '7px', borderRadius: '5px', maxWidth: '600px' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1em', borderBottom: '1px solid #e0e0e0' }}>
        <div style={{ display: 'flex', flexShrink: 1, alignItems: 'center', fontSize: '1.1em', minWidth: '160px', width: '300px', maxWidth: '300px' }}><b>{props.customerCreditSection.sectionTitle} Items</b></div>
        <div style={{ display: 'flex', alignItems: 'center', maxWidth: '120px', minWidth: '90px' }}>Bureau</div>
        <div style={{ display: 'flex', alignItems: 'center', maxWidth: '120px', minWidth: '90px' }}>Override</div>
      </div>
      {props.customerCreditSection.customerCreditRows.map((row, key) => {
        return (
          <CustomerCreditRow
            {...props}
            key={key}
            customerCreditRow={row}
            customFields={props.customFields}
            ignoreBottomBorder={key === props.customerCreditSection.customerCreditRows.length - 1}
          />
        );
      })}
    </div>
  );
}

interface ICustomerCreditRowProps {
  customerCredit: ICustomerCredit;
  customFields: IApplicationCustomField[] | ICustomerCustomField[];
  customerCreditRow: ICustomerCreditRow;
  onUpdateCustomerCredit(field: ICustomerCreditUpdatePair): void;
  onUpdateOverrideValue(field: ICustomerCreditUpdatePair): void;
  ignoreBottomBorder: boolean;
}

const CustomerCreditRow: React.FC<ICustomerCreditRowProps> = (props) => {
  const allowUserToOverrideValue: boolean = props.customFields.some((cf: IEntityCustomField) => cf.customField.customerCreditOverrideField?.toLowerCase() === props.customerCreditRow.variableName.toLowerCase());
  return (
    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1em', maxWidth: '600px' }}>
      <div style={{ display: 'flex', flexShrink: 1, alignItems: 'center', minWidth: '160px', width: '300px', maxWidth: '300px', fontSize: '0.9em' }}>
        <Tooltip title={props.customerCreditRow.toolTipText}>
          <InfoOutlinedIcon
            fontSize="small"
            color="primary"
            style={{ marginRight: '8px', cursor: 'help' }}
          />
        </Tooltip>
        {props.customerCreditRow.itemTitle}
      </div>
      <div style={{ maxWidth: '120px', minWidth: '90px' }}>
        <TextBox
          // Always pass a defined value: an undefined/null value turns the Kendo TextBox uncontrolled and
          // the DOM keeps whatever the previously selected applicant had in this input.
          value={(props.customerCredit[props.customerCreditRow.variableName] as string | number | readonly string[] | undefined) ?? ''}
          style={{ cursor: 'not-allowed', fontSize: '0.9em' }}
          readOnly={true}
          title={'Bureau field is not editable'}
          size={'small'}
        />
      </div>
      <div style={allowUserToOverrideValue ? { maxWidth: '120px', minWidth: '90px' } : { maxWidth: '120px', minWidth: '90px', visibility: 'hidden' }}>
        <TextBox
          value={(props.customFields as IEntityCustomField[]).find(cf => cf.customField.customerCreditOverrideField?.toLowerCase() === props.customerCreditRow.variableName.toLowerCase())?.value ?? ''}
          onChange={(e) => props.onUpdateOverrideValue({ variableName: (props.customFields as IEntityCustomField[]).find(cf => cf.customField.customerCreditOverrideField?.toLowerCase() === props.customerCreditRow.variableName.toLowerCase())?.customField.customerCreditOverrideField ?? '', value: e.target.value ? Number(e.target.value) : undefined })}
          readOnly={!allowUserToOverrideValue}
          title={`Enter a value here to override the value for ${props.customerCreditRow.itemTitle}`}
          size={'small'}
          style={{ fontSize: '0.9em' }}
          type="number"
        />
      </div>
    </div>
  );
}